/**
 * Speech-only number normalization for Web Speech (SpeechSynthesisUtterance).
 *
 * Chrome TTS reads a thousands comma literally ("590,849" -> "five ninety comma
 * eight forty-nine"), and simply stripping the comma makes it spell the digits
 * out instead ("five nine zero ..."). Converting the number to American English
 * words is what makes it read as a whole value (ATAI-239).
 *
 * Apply this ONLY to text handed to the speech engine — displayed chat/UI text
 * keeps its own formatting.
 */
const THOUSANDS_GROUPED_NUMBER_PATTERN =
  /\$?\b\d{1,3}(?:[,，]\d{3})+(?:\.\d+)?\b/g;
const THOUSANDS_GROUP_SEPARATOR_PATTERN = /[,，]/g;
const SPOKEN_NUMBER_SMALL = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const SPOKEN_NUMBER_TENS = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
];
const SPOKEN_NUMBER_SCALES = [
  { value: 1000000000, label: 'billion' },
  { value: 1000000, label: 'million' },
  { value: 1000, label: 'thousand' },
];

const numberUnderOneThousandToSpokenEnglish = (number) => {
  const parts = [];
  const hundreds = Math.floor(number / 100);
  const remainder = number % 100;

  if (hundreds) parts.push(`${SPOKEN_NUMBER_SMALL[hundreds]} hundred`);

  if (remainder >= 20) {
    const tens = Math.floor(remainder / 10);
    const ones = remainder % 10;
    parts.push(
      ones
        ? `${SPOKEN_NUMBER_TENS[tens]}-${SPOKEN_NUMBER_SMALL[ones]}`
        : SPOKEN_NUMBER_TENS[tens]
    );
  } else if (remainder > 0) {
    parts.push(SPOKEN_NUMBER_SMALL[remainder]);
  }

  return parts.join(' ');
};

const numberToSpokenEnglish = (number) => {
  if (number === 0) return SPOKEN_NUMBER_SMALL[0];
  if (!Number.isSafeInteger(number) || number < 0) return String(number);

  const parts = [];
  let remainder = number;

  SPOKEN_NUMBER_SCALES.forEach(({ value, label }) => {
    if (remainder >= value) {
      const scaleValue = Math.floor(remainder / value);
      parts.push(`${numberToSpokenEnglish(scaleValue)} ${label}`);
      remainder %= value;
    }
  });

  if (remainder) parts.push(numberUnderOneThousandToSpokenEnglish(remainder));

  return parts.join(' ');
};

const decimalDigitsToSpokenEnglish = (digits) =>
  digits
    .split('')
    .map((digit) => SPOKEN_NUMBER_SMALL[Number(digit)])
    .join(' ');

const normalizeSpokenNumbers = (text) =>
  text.replace(THOUSANDS_GROUPED_NUMBER_PATTERN, (value) => {
    const numericToken = value.replace(/^\$/, '');
    const [integerPart, decimalPart] = numericToken.split('.');
    const integerValue = Number(integerPart.replace(THOUSANDS_GROUP_SEPARATOR_PATTERN, ''));
    const spokenInteger = numberToSpokenEnglish(integerValue);
    const spoken = decimalPart
      ? `${spokenInteger} point ${decimalDigitsToSpokenEnglish(decimalPart)}`
      : spokenInteger;

    return value.startsWith('$') ? `${spoken} dollars` : spoken;
  });

export { numberToSpokenEnglish };

export default normalizeSpokenNumbers;
